import {
  Flex,
  FormControl,
  Input,
  InputGroup,
  InputLeftElement,
  SlideFade,
  Stack,
  Text,
  useColorModeValue,
  Select,
  Box,
  Image,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  Button,
  useDisclosure,
  Spinner,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { AiOutlinePhone, AiOutlineCamera, AiOutlineCheckCircle } from "react-icons/ai";
import Container from "../../components/Container";
import { PrimaryButton } from "../../components/CustomButton";
import { errorNotifier } from "../../components/NotificationHandler";
import { Link } from "react-router-dom";
import { COLORS } from "../../constants/colors";
import { PUBLIC_PATHS } from "../../app/constants";
import { fetchStreetsData, signupTenant } from "./service/signup";

const { LOGIN } = PUBLIC_PATHS;

const Signup = () => {
  const cardBg = useColorModeValue(COLORS.primary, COLORS.white);
  const pageBg = useColorModeValue(COLORS.white, COLORS.black);

  // Signup form states
  const [streetId, setStreetId] = useState("");
  const [houseId, setHouseId] = useState(""); // Changed from houseNumber to houseId
  const [displayHouseNumber, setDisplayHouseNumber] = useState(""); // For display purposes
  const [tenantName, setTenantName] = useState("");
  const [tenantPhone, setTenantPhone] = useState("");
  const [email, setEmail] = useState("");
  const [designation, setDesignation] = useState("");
  const [loading, setLoading] = useState(false);
  const [vehicleType, setVehicleType] = useState("");
  const [vehicleModel, setVehicleModel] = useState("");
  const [plateNumber, setPlateNumber] = useState("");

  // Backend data states
  const [streetsData, setStreetsData] = useState({});
  const [housesMap, setHousesMap] = useState({}); // Map to store house number -> house ID mapping
  const [loadingStreets, setLoadingStreets] = useState(true);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const [isRedirecting, setIsRedirecting] = useState(false);

  // Fetch streets and house numbers from backend on component mount
// Fetch streets and house numbers from backend on component mount
useEffect(() => {
  console.log("Starting to fetch streets data...");
  let isSubscribed = true;

  const loadStreetsData = async () => {
    try {
      await fetchStreetsData(
        (data) => {
          if (isSubscribed) {
            console.log("Received streets data:", data);
            setStreetsData(data);
            
            // Create a house mapping for each street using the data returned by fetchStreetsData
            const houseMapping = {};
            Object.entries(data).forEach(([streetId, streetData]) => {
              houseMapping[streetId] = streetData.houseDetails || {};
            });
            setHousesMap(houseMapping);
          }
        },
        (loading) => {
          if (isSubscribed) {
            setLoadingStreets(loading);
          }
        }
      );
    } catch (error) {
      console.error("Failed to fetch streets data:", error);
      if (isSubscribed) {
        setLoadingStreets(false);
        errorNotifier("Could not load streets data.");
      }
    }
  };

  loadStreetsData();

  return () => {
    isSubscribed = false;
  };
}, []);

  // Debug log when streetsData changes
  useEffect(() => {
    console.log("Current streetsData state:", streetsData);
  }, [streetsData]);

  const handleStreetChange = (e) => {
    const selectedStreetId = e.target.value;
    setStreetId(selectedStreetId); // Set the street ID to be sent to the backend
    setHouseId(""); // Reset house ID when street changes
    setDisplayHouseNumber(""); // Reset display house number
  };

  const handleHouseChange = (e) => {
    const selectedHouseNumber = e.target.value;
    setDisplayHouseNumber(selectedHouseNumber);
    
    // Get the house ID from our mapping
    if (housesMap[streetId] && housesMap[streetId][selectedHouseNumber]) {
      setHouseId(housesMap[streetId][selectedHouseNumber]);
    } else {
      // Fallback to using the house number if mapping fails
      setHouseId(selectedHouseNumber);
      console.warn("House ID mapping not found for", selectedHouseNumber, "using house number instead");
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();

    const payload = {
      houseId: houseId, // Send the house ID instead of number
      streetId,
      houseUnit: "",
      tenantName,
      tenantPhone,
      email,
      designation,
      type: vehicleType,
      model: vehicleModel,
      plateNumber: plateNumber,
    };

    if (!houseId || !streetId || !tenantName || !tenantPhone || !email || !designation ) {
      errorNotifier("Please fill in all fields");
      return;
    }

    const success = await signupTenant(payload, setLoading);
    if (success) {
      onOpen();
    }
  };

  const handleContinue = () => {
    setIsRedirecting(true);
    onClose();
    setTimeout(() => {
      window.location.href = LOGIN;
    }, 1000);
  };


  return (
    <SlideFade in={true} offsetX="-5000px">
      <Container bg={pageBg}>
        <Stack w="100%" as="section" pt="15vh" h="100%">
          <Text color={cardBg} fontWeight="600" fontSize="2.7rem">
            Sign Up
          </Text>
          <Text color={cardBg} fontWeight="500" fontSize="1.2rem">
            Kindly fill up the details below
          </Text>
          <FormControl w="100%">
          <Flex my="7" gap="4">
                {/* Street Select */}
                <Box position="relative" flex="1">
                <InputGroup flex="1">
                  <Select
                    {...inputStyles}
                    borderColor={COLORS.green}
                    placeholder="Select Street"
                    _placeholder={{ color: COLORS.green }}
                    value={streetId}
                    onChange={handleStreetChange}
                    isDisabled={loadingStreets}
                  >
                    {Object.values(streetsData)
                      .sort((a, b) => a.streetName.localeCompare(b.streetName))
                      .map(({ streetName, streetId }) => (
                        <option key={streetId} value={streetId}>
                          {streetName}
                        </option>
                      ))}
                  </Select>
                </InputGroup>

<Text position="absolute" 
                  top="-8px" 
                  right="8px" 
                  color="red.500" 
                  fontWeight="600" 
                  fontSize="24px"
                  bg={pageBg}
                  px="2px"
                > 
                  * 
                </Text>
                </Box>

                {/* House Number Select */}
                <Box position="relative" flex="1">
                <InputGroup flex="1">
                  <Select
                    {...inputStyles}
                    borderColor={COLORS.green}
                    placeholder="Select House"
                    _placeholder={{ color: COLORS.green }}
                    value={displayHouseNumber}
                    onChange={handleHouseChange}
                    isDisabled={!streetId}
                  >
                    {streetId &&
                      streetsData[streetId]?.houses.map((number) => (
                        <option key={number} value={number}>
                          {number}
                        </option>
                      ))}
                  </Select>
                </InputGroup>
                <Text 
                    position="absolute" 
                    top="-8px" 
                    right="8px" 
                    color="red.500" 
                    fontWeight="600" 
                    fontSize="24px"
                    bg={pageBg}
                    px="2px"
                  > 
                    * 
                  </Text>
                </Box>
              </Flex>

              {/* Designation Select Field */}
              <Box position="relative" flex="1">
              <InputGroup my="7">
                <Select
                  {...inputStyles}
                  borderColor={COLORS.green}
                  placeholder="Designation"
                  _placeholder={{ color: COLORS.green }}
                  value={designation}
                  onChange={(e) => setDesignation(e.target.value)}
                >
                  <option value="tenant">Tenant</option>
                  <option value="landlord">Landlord</option>
                  <option value="agent">Agent</option>
                  <option value="business">Business</option>
                </Select>
              </InputGroup>
              <Text 
                    position="absolute" 
                    top="-8px" 
                    right="8px" 
                    color="red.500" 
                    fontWeight="600" 
                    fontSize="24px"
                    bg={pageBg}
                    px="2px"
                  > 
                    * 
                  </Text>
              </Box>
              {/* Full Name Input Field */}
              <Box position="relative" flex="1">
              <InputGroup my="7">
                <Input
                  {...inputStyles}
                  borderColor={COLORS.green}
                  placeholder="Full Name"
                  _placeholder={{ color: COLORS.green }}
                  value={tenantName}
                  onChange={(e) => setTenantName(e.target.value)}
                />
              </InputGroup>
              <Text 
                    position="absolute" 
                    top="-8px" 
                    right="8px" 
                    color="red.500" 
                    fontWeight="600" 
                    fontSize="24px"
                    bg={pageBg}
                    px="2px"
                  > 
                    * 
                  </Text>
              </Box>

              {/* Phone Number Input Field */}
              <Box position="relative" flex="1">
              <InputGroup my="7">
                <Input
                  {...inputStyles}
                  borderColor={COLORS.green}
                  placeholder="Phone Number"
                  _placeholder={{ color: COLORS.green }}
                  value={tenantPhone}
                  type="number"
                  onChange={(e) => setTenantPhone(e.target.value)}
                />
                <InputLeftElement children={<AiOutlinePhone />} mt="10px" />
                </InputGroup>
              <Text 
                    position="absolute" 
                    top="-8px" 
                    right="8px" 
                    color="red.500" 
                    fontWeight="600" 
                    fontSize="24px"
                    bg={pageBg}
                    px="2px"
                  > 
                    * 
                  </Text>
              </Box>
              {/* Email Input Field */}
              <Box position="relative" flex="1">
              <InputGroup my="7">
                <Input
                  {...inputStyles}
                  borderColor={COLORS.green}
                  placeholder="Email"
                  _placeholder={{ color: COLORS.green }}
                  value={email}
                  type="email"
                  onChange={(e) => setEmail(e.target.value)}
                />
              </InputGroup>
              <Text 
                    position="absolute" 
                    top="-8px" 
                    right="8px" 
                    color="red.500" 
                    fontWeight="600" 
                    fontSize="24px"
                    bg={pageBg}
                    px="2px"
                  > 
                    * 
                  </Text>
              </Box>



            {/* Signup Button */}
            <PrimaryButton
              loadingText="Signing you up"
              btnText="Sign up"
              isLoading={loading}
              onClick={handleSignup}
              h="60px"
              fontSize="18px"
              rounded="2xl"
            />

            {/* Login Link */}
            <Text
              fontSize="16px"
              textAlign="center"
              fontWeight={600}
              color={cardBg}
              mb="20px"
            >
              Already have an account?{" "}
              <Link
                to={LOGIN}
                style={{ color: COLORS.green, textDecoration: "underline" }}
              >
                Log In
              </Link>
            </Text>
          </FormControl>
        </Stack>
      </Container>

            {/* Success Modal */}
            <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent bg={COLORS.green} color="white" borderRadius="xl">
          <ModalBody p={8}>
            <Stack spacing={4} align="center" textAlign="center">
              <Icon as={AiOutlineCheckCircle} boxSize="12" />
              <Text fontSize="2xl" fontWeight="bold">
                Well Done!
              </Text>
              <Text>
                Your account has been created successfully. Please check your email for your default User Code/Password. You can change this at your first login.
              </Text>
              <Button
                bg="white"
                color={COLORS.green}
                _hover={{ bg: "gray.100" }}
                onClick={handleContinue}
                size="lg"
                w="full"
                mt={4}
                isLoading={isRedirecting}
                loadingText="Redirecting..."
              >
                Continue to Login
              </Button>
            </Stack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </SlideFade>
  );
};

export default Signup;

export const inputStyles = {
  bg: "white",
  rounded: "2xl",
  h: "60px",
  color: "#262636",
};